/**
 * /trial-balance — accountant's first stop. All accounts with their period
 * (or YTD) debit/credit totals + computed balance, plus a balanced-totals
 * footer. Filter by fiscal period via dropdown; default = all periods (YTD).
 *
 * Dimension slice: pick any active GL dimension + one of its values to see
 * only the lines tagged with that segment (e.g. OFFICE = New York). Useful
 * for profitability-by-office, P&L-by-fund, or cost-by-division views.
 * Note: a dimension-sliced TB will not balance — only tagged lines are summed.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";

import {
  fiscalYearsListQueryOptions,
  glDimensionDetailQueryOptions,
  glDimensionsQueryOptions,
  trialBalanceQueryOptions,
} from "@/lib/queries";
import { TrialBalanceGrid } from "@/grids/TrialBalanceGrid";
import type { FiscalPeriod, GlDimensionValue } from "@/lib/types";

export const Route = createFileRoute("/_auth/trial-balance")({
  component: TrialBalancePage,
});

function fmtMoney(n: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Flatten a dimension value tree into a flat option list with depth cues. */
function flattenValues(
  nodes: GlDimensionValue[],
  depth = 0,
): Array<{ id: string; label: string }> {
  return nodes.flatMap((n) => [
    { id: n.id, label: " ".repeat(depth * 3) + (depth > 0 ? "└ " : "") + n.name },
    ...flattenValues(n.children, depth + 1),
  ]);
}

function TrialBalancePage() {
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  // Dimension filter
  const [dimCode, setDimCode] = useState<string>("");
  const [dimValueId, setDimValueId] = useState<string>("");

  const yearsQuery = useQuery(fiscalYearsListQueryOptions());
  const dimsListQuery = useQuery(glDimensionsQueryOptions());
  const activeDims = useMemo(
    () => (dimsListQuery.data ?? []).filter((d) => d.is_active),
    [dimsListQuery.data],
  );

  // Load the selected dim's detail (values tree) — enabled only when a dim is chosen
  const selectedDimId = activeDims.find((d) => d.code === dimCode)?.id ?? "";
  const [dimDetailQuery] = useQueries({
    queries: [glDimensionDetailQueryOptions(selectedDimId)],
  });

  const flatValues = useMemo(
    () => (dimDetailQuery.data ? flattenValues(dimDetailQuery.data.values) : []),
    [dimDetailQuery.data],
  );

  const tbQuery = useQuery(
    trialBalanceQueryOptions(
      selectedPeriodId,
      dimCode || undefined,
      dimValueId || undefined,
    ),
  );

  // Flatten the period selector: "All periods (YTD)" + each period nested under its year
  const periodsFlat = useMemo<{ year: number; period: FiscalPeriod }[]>(() => {
    const out: { year: number; period: FiscalPeriod }[] = [];
    (yearsQuery.data ?? []).forEach((y) => {
      y.periods.forEach((p) => out.push({ year: y.year, period: p }));
    });
    return out;
  }, [yearsQuery.data]);

  const tb = tbQuery.data;
  const totalDebit = tb ? Number(tb.total_debit) : 0;
  const totalCredit = tb ? Number(tb.total_credit) : 0;
  const difference = totalDebit - totalCredit;

  // When a dimension is active, the TB won't balance — that's expected
  const dimActive = Boolean(dimCode && dimValueId);
  const selectedDimMeta = activeDims.find((d) => d.code === dimCode);
  const selectedValueLabel = flatValues.find((v) => v.id === dimValueId)?.label.trim();

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Trial Balance</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            All postable accounts · debit/credit totals · YTD or by period
          </span>
          <div className="flex-1" />
          {tbQuery.isFetching && (
            <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-end gap-4 flex-wrap dark:bg-gray-900 dark:border-gray-800">
        {/* Period filter */}
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">
            Period
          </label>
          <select
            value={selectedPeriodId ?? ""}
            onChange={(e) => setSelectedPeriodId(e.target.value || null)}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white min-w-[280px] dark:bg-gray-900 dark:border-gray-700"
          >
            <option value="">All periods (YTD across all open years)</option>
            {periodsFlat.map(({ year, period }) => (
              <option key={period.id} value={period.id}>
                FY{year} · Period {period.period_no} ({period.start_date} → {period.end_date}) · {period.status}
              </option>
            ))}
          </select>
        </div>

        {/* Dimension filter — only shown when dimensions are configured */}
        {activeDims.length > 0 && (
          <>
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">
                Dimension
              </label>
              <select
                value={dimCode}
                onChange={(e) => { setDimCode(e.target.value); setDimValueId(""); }}
                className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
              >
                <option value="">— all segments —</option>
                {activeDims.map((d) => (
                  <option key={d.id} value={d.code}>{d.name}</option>
                ))}
              </select>
            </div>

            {dimCode && (
              <div className="flex flex-col">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">
                  {selectedDimMeta?.name ?? "Value"}
                </label>
                <select
                  value={dimValueId}
                  onChange={(e) => setDimValueId(e.target.value)}
                  disabled={dimDetailQuery.isLoading}
                  className="border border-indigo-400 rounded-md px-2 py-1 text-sm bg-white min-w-[200px] dark:bg-gray-900 dark:border-indigo-600"
                >
                  <option value="">— pick value —</option>
                  {flatValues.map((v) => (
                    <option key={v.id} value={v.id}>{v.label}</option>
                  ))}
                </select>
              </div>
            )}

            {dimActive && (
              <button
                type="button"
                onClick={() => { setDimCode(""); setDimValueId(""); }}
                className="text-xs text-indigo-600 hover:text-indigo-800 border border-indigo-300 rounded-md px-2 py-1 self-end mb-0.5 dark:text-indigo-400"
              >
                ✕ Clear filter
              </button>
            )}
          </>
        )}

        <div className="text-xs text-slate-500 ml-2 dark:text-slate-400 self-end pb-0.5">
          {tb?.rows.length ?? 0} accounts with activity
        </div>
      </div>

      {/* Active dimension filter banner */}
      {dimActive && selectedDimMeta && selectedValueLabel && (
        <div className="bg-indigo-50 border-b border-indigo-200 px-6 py-2 flex items-center gap-2 text-sm dark:bg-indigo-950/30 dark:border-indigo-800">
          <span className="text-indigo-700 dark:text-indigo-300 font-medium">
            Sliced by:
          </span>
          <span className="font-mono text-xs bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 px-2 py-0.5 rounded border border-indigo-200 dark:border-indigo-700">
            {selectedDimMeta.name} = {selectedValueLabel}
          </span>
          <span className="text-indigo-500 dark:text-indigo-400 text-xs">
            · Only journal lines tagged with this segment are summed · totals will not balance
          </span>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 px-6 py-4 overflow-hidden">
        {tbQuery.error && (
          <div className="text-sm text-red-600">
            Error: {(tbQuery.error as Error).message}
          </div>
        )}
        {tb && (
          <TrialBalanceGrid rows={tb.rows} loading={tbQuery.isLoading} />
        )}
      </div>

      {/* Footer totals */}
      {tb && (
        <div
          className={`border-t px-6 py-3 flex items-center gap-6 text-sm ${
            dimActive
              ? "bg-indigo-50 border-indigo-200"
              : tb.is_balanced
              ? "bg-green-50 border-green-200"
              : "bg-amber-50 border-amber-300"
          }`}
        >
          <div>
            <span className="text-xs uppercase tracking-wide text-slate-500 mr-2 dark:text-slate-400">
              Total Debit
            </span>
            <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">
              {fmtMoney(totalDebit)}
            </span>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wide text-slate-500 mr-2 dark:text-slate-400">
              Total Credit
            </span>
            <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">
              {fmtMoney(totalCredit)}
            </span>
          </div>
          {!dimActive && !tb.is_balanced && (
            <div>
              <span className="text-xs uppercase tracking-wide text-amber-700 mr-2">
                Difference
              </span>
              <span className="font-mono font-bold text-amber-800">
                {fmtMoney(difference)}
              </span>
            </div>
          )}
          <div className="flex-1" />
          {dimActive ? (
            <span className="px-2 py-0.5 text-xs uppercase tracking-wide font-semibold rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200">
              Dimension slice · unbalanced by design
            </span>
          ) : (
            <span
              className={`px-2 py-0.5 text-xs uppercase tracking-wide font-semibold rounded ${
                tb.is_balanced
                  ? "bg-green-100 text-green-800"
                  : "bg-amber-100 text-amber-800"
              }`}
            >
              {tb.is_balanced ? "✓ Balanced" : "⚠ Out of balance"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
